/**
 * Strip the state and the pincode off the end of an address.
 *
 * Addresses are built as "flat, road, postOffice, area, district, state,
 * pincode". The affidavit wants only up to the district, so the last two
 * pieces come off. Mirrors `stripStateAndPincode` in the API's
 * affidavit-certificate.service.ts (used by the XLSX export) — keep the two in
 * step.
 *
 * The tail is matched, not counted, because empty pieces are dropped when the
 * address is built: an address with no pincode ends at the state, and one with
 * neither already ends at the district and must be left alone.
 */

// "TAMIL NADU" / "TAMILNADU" and "JAMMU & KASHMIR" / "JAMMU AND KASHMIR"
// have to land on the same key, so & becomes AND and the gaps go.
const normalize = (value: string) =>
  value
    .toUpperCase()
    .replace(/&/g, "AND")
    .replace(/[^A-Z0-9]/g, "");

const STATE_NAMES = [
  "ANDAMAN AND NICOBAR ISLANDS",
  "ANDHRA PRADESH",
  "ARUNACHAL PRADESH",
  "ASSAM",
  "BIHAR",
  "CHANDIGARH",
  "CHHATISHGARH",
  "CHHATTISGARH",
  "DADRA AND NAGAR HAVELI",
  "DAMAN AND DIU",
  "DELHI",
  "GOA",
  "GUJARAT",
  "HARYANA",
  "HIMACHAL PRADESH",
  "JAMMU AND KASHMIR",
  "JHARKHAND",
  "KARNATAKA",
  "KERALA",
  "LADAKH",
  "LAKHSWADEEP",
  "LAKSHADWEEP",
  "MADHYA PRADESH",
  "MAHARASHTRA",
  "MANIPUR",
  "MEGHALAYA",
  "MIZORAM",
  "NAGALAND",
  "NEW DELHI",
  "ODISHA",
  "ORISSA",
  "PONDICHERRY",
  "PUDUCHERRY",
  "PUNJAB",
  "RAJASTHAN",
  "SIKKIM",
  "TAMIL NADU",
  "TAMILNADU",
  "TELANGANA",
  "TRIPURA",
  "UTTAR PRADESH",
  "UTTARAKHAND",
  "UTTRAKHAND",
  "WEST BENGAL",
];

const STATE_KEYS = new Set(STATE_NAMES.map(normalize));

// A bare six digit pincode, or one labelled "PIN - 452001" / "PINCODE 452001".
const isPincode = (part: string) =>
  /^(?:PIN(?:\s*CODE)?)?\s*[-:]?\s*\d{6}$/i.test(part.trim());

export const stripStateAndPincode = (address?: string | null) => {
  if (!address || typeof address !== "string") return "";

  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  while (parts.length) {
    const last = parts[parts.length - 1];
    if (isPincode(last) || STATE_KEYS.has(normalize(last))) {
      parts.pop();
      continue;
    }
    break;
  }

  return parts.join(", ");
};
